import React from "react";

const WindIndicator = ({ speed = 0, direction = 0, gust }) => {
  const getWindSpeedColor = (speed) => {
    if (speed < 5) return "#4CAF50"; // Light breeze
    if (speed < 10) return "#2196F3"; // Moderate breeze
    if (speed < 20) return "#FF9800"; // Strong breeze
    return "#F44336"; // High winds
  };

  const getWindDirection = (degrees) => {
    const directions = [
      "N",
      "NNE",
      "NE",
      "ENE",
      "E",
      "ESE",
      "SE",
      "SSE",
      "S",
      "SSW",
      "SW",
      "WSW",
      "W",
      "WNW",
      "NW",
      "NNW",
    ];
    const index = Math.round((degrees || 0) / 22.5) % 16;
    return directions[index];
  };

  const getWindDescription = (speed) => {
    if (speed < 2) return "Calm";
    if (speed < 5) return "Light breeze";
    if (speed < 10) return "Moderate breeze";
    if (speed < 20) return "Strong breeze";
    return "High winds";
  };

  // Ensure we have numeric values
  const windSpeed = typeof speed === "number" ? speed : 0;
  const windDirection = typeof direction === "number" ? direction : 0;
  const windDir = getWindDirection(windDirection);
  const windDesc = getWindDescription(windSpeed);
  const windColor = getWindSpeedColor(windSpeed);

  return (
    <div className="bg-white rounded-lg p-4">
      <div className="flex flex-col items-center">
        <div className="relative w-32 h-32 mb-4">
          {/* Compass circle */}
          <div className="absolute inset-0 rounded-full border border-gray-200">
            {/* Cardinal directions */}
            {["N", "E", "S", "W"].map((dir, i) => (
              <div
                key={dir}
                className="absolute text-xs text-gray-400"
                style={{
                  top:
                    dir === "N"
                      ? "4px"
                      : dir === "S"
                      ? "calc(100% - 16px)"
                      : "calc(50% - 8px)",
                  left:
                    dir === "W"
                      ? "4px"
                      : dir === "E"
                      ? "calc(100% - 12px)"
                      : "calc(50% - 4px)",
                }}
              >
                {dir}
              </div>
            ))}

            {/* Tick marks - 24 marks for every 15 degrees */}
            {Array.from({ length: 24 }).map((_, i) => (
              <div
                key={i}
                className="absolute top-1/2 left-1/2 w-full h-0.5"
                style={{
                  transform: `translate(-50%, -50%) rotate(${i * 15}deg)`,
                }}
              >
                <div className="absolute right-0 w-1 h-0.5 bg-gray-200" />
                <div className="absolute left-0 w-1 h-0.5 bg-gray-200" />
              </div>
            ))}

            {/* Wind direction arrow */}
            <div
              className="absolute top-1/2 left-1/2 h-24 w-2 rounded-full origin-center"
              style={{
                transform: `translate(-50%, -50%) rotate(${
                  windDirection + 180
                }deg)`,
                backgroundColor: windColor,
              }}
            >
              {/* Arrow head */}
              <div
                className="absolute bottom-0 left-1/2 w-0 h-0"
                style={{
                  transform: "translate(-50%, 0)",
                  borderLeft: "6px solid transparent",
                  borderRight: "6px solid transparent",
                  borderTop: `8px solid ${windColor}`,
                }}
              />
            </div>
          </div>
        </div>

        <div className="text-center">
          <div
            className="text-3xl font-medium mb-1"
            style={{ color: windColor }}
          >
            {windSpeed.toFixed(1)} <span className="text-base">km/h</span>
          </div>
          <div className="text-sm text-gray-500">
            {windDir} • {windDesc}
          </div>
          {gust && (
            <div className="text-xs text-gray-400 mt-1">
              Gust: {gust.toFixed(1)} km/h
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WindIndicator;
